import React, { useEffect, useId, useRef, useState } from 'react';
import { Form as FinalForm, Field } from 'react-final-form';
import classNames from 'classnames';

import { useIntl } from '../../../../util/reactIntl';
import { isMainSearchTypeKeywords } from '../../../../util/search';
import { searchSuggestions } from '../../../../util/api';
import { hideTidioChat, showTidioChat } from '../../../../util/tidioChat';
import { getRecentlyViewedListings } from '../../../../util/recentlyViewedListings';

import { Form, LocationAutocompleteInput } from '../../../../components';

import IconSearchDesktop from './IconSearchDesktop';
import LiveSearchMegaMenu from './LiveSearchMegaMenu';
import css from './TopbarSearchForm.module.css';

const identity = v => v;

const KeywordSearchField = props => {
  const {
    keywordSearchWrapperClasses,
    iconClass,
    isMobile = false,
    inputRef,
    placeholder,
    onFocus,
    onBlur,
    onKeywordChange,
    onKeyDown,
    uniqueId,
  } = props;
  return (
    <div className={keywordSearchWrapperClasses}>
      <button className={css.searchSubmit}>
        <div className={iconClass}>
          <IconSearchDesktop />
        </div>
      </button>
      <Field
        name="keywords"
        render={({ input }) => {
          const handleChange = event => {
            input.onChange(event);
            onKeywordChange(event?.target?.value || '');
          };

          return (
            <input
              className={isMobile ? css.mobileInput : css.desktopInput}
              {...input}
              id={uniqueId}
              data-testid={isMobile ? 'keyword-search-mobile' : 'keyword-search'}
              ref={inputRef}
              type="text"
              placeholder={placeholder}
              autoComplete="off"
              onChange={handleChange}
              onKeyDown={onKeyDown}
              onFocus={onFocus}
              onBlur={onBlur}
            />
          );
        }}
      />
    </div>
  );
};

const LocationSearchField = props => {
  const { desktopInputRootClass, intl, isMobile = false, inputRef, onLocationChange, placeholder, onFocus, onBlur } = props;
  return (
    <Field
      name="location"
      format={identity}
      render={({ input, meta }) => {
        const { onChange, ...restInput } = input;

        // Merge the standard onChange function with custom behaviur. A better solution would
        // be to use the FormSpy component from Final Form and pass onChange to the
        // onChange prop but that breaks due to insufficient subscription handling.
        // See: https://github.com/final-form/react-final-form/issues/159
        const searchOnChange = value => {
          onChange(value);
          onLocationChange(value);
        };

        return (
          <LocationAutocompleteInput
            className={isMobile ? css.mobileInputRoot : desktopInputRootClass}
            iconClassName={isMobile ? css.mobileIcon : css.desktopIcon}
            inputClassName={isMobile ? css.mobileInput : css.desktopInput}
            predictionsClassName={isMobile ? css.mobilePredictions : css.desktopPredictions}
            predictionsAttributionClassName={isMobile ? css.mobilePredictionsAttribution : null}
            placeholder={placeholder}
            closeOnBlur={!isMobile}
            inputRef={inputRef}
            input={{ ...restInput, onChange: searchOnChange, onFocus, onBlur }}
            meta={meta}
          />
        );
      }}
    />
  );
};

/**
 * The main search form for the Topbar.
 *
 * @component
 * @param {Object} props
 * @param {string?} props.className add more style rules in addition to components own css.root
 * @param {string?} props.rootClassName overwrite components own css.root
 * @param {string?} props.desktopInputRoot root class for desktop form input
 * @param {Function} props.onSubmit
 * @param {boolean} props.isMobile
 * @param {Object} props.appConfig
 * @returns {JSX.Element} search form element
 */
const TopbarSearchForm = props => {
  const searchInpuRef = useRef(null);
  const requestCounterRef = useRef(0);
  const pageLockRef = useRef({ scrollY: 0, locked: false });
  const intl = useIntl();
  const uniqueInputId = useId();
  const { appConfig, onSubmit, hidePlaceholder = false, ...restOfProps } = props;
  const isMobileForm = Boolean(props.isMobile);

  // Dynamic placeholder logic (typewriter effect)
  const defaultBasePlaceholder = intl.formatMessage({ id: 'TopbarSearchForm.placeholder' });
  const configuredExamples = appConfig?.topbar?.searchBar?.placeholderExamples;
  const placeholderExamples = Array.isArray(configuredExamples) && configuredExamples.length > 0
    ? configuredExamples
    : [
        'Rechercher des appareils connectés…',
        "Essayez 'Aqara' ou 'TP-Link'…",
        'Rechercher une marque, une catégorie ou une fonction…'      ];
  const typingSpeedMs = Number(appConfig?.topbar?.searchBar?.typingSpeedMs) > 0
    ? Number(appConfig?.topbar?.searchBar?.typingSpeedMs)
    : 40;
  const deletingSpeedMs = Number(appConfig?.topbar?.searchBar?.deletingSpeedMs) > 0
    ? Number(appConfig?.topbar?.searchBar?.deletingSpeedMs)
    : 25;
  const pauseAtEndMs = Number(appConfig?.topbar?.searchBar?.pauseAtEndMs) > 0
    ? Number(appConfig?.topbar?.searchBar?.pauseAtEndMs)
    : 1300;
  const pauseBeforeStartMs = Number(appConfig?.topbar?.searchBar?.pauseBeforeStartMs) > 0
    ? Number(appConfig?.topbar?.searchBar?.pauseBeforeStartMs)
    : 300;

  const [exampleIndex, setExampleIndex] = useState(0);
  const [charIndex, setCharIndex] = useState(0);
  const [isDeleting, setIsDeleting] = useState(false);
  const [displayText, setDisplayText] = useState('');
  const [isInputFocused, setIsInputFocused] = useState(false);
  const [keywordQuery, setKeywordQuery] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [isLoadingSuggestions, setIsLoadingSuggestions] = useState(false);
  const [showLiveSuggestions, setShowLiveSuggestions] = useState(false);
  const [activeSuggestionIndex, setActiveSuggestionIndex] = useState(-1);
  const [recentlyViewedItems, setRecentlyViewedItems] = useState([]);

  const refreshRecentlyViewed = () => {
    setRecentlyViewedItems(getRecentlyViewedListings());
  };

  // Handle input focus - stop animation and clear placeholder
  const handleInputFocus = () => {
    setIsInputFocused(true);
    setDisplayText('');
    setCharIndex(0);
    setIsDeleting(false);
    if (isMainSearchTypeKeywords(appConfig)) {
      refreshRecentlyViewed();
      setShowLiveSuggestions(true);
    }
  };

  // Handle input blur - resume animation after a delay
  const handleInputBlur = () => {
    if (isMobileForm && isMainSearchTypeKeywords(appConfig)) {
      // On mobile we keep the sheet open while user navigates suggestions.
      return;
    }
    setIsInputFocused(false);
    setTimeout(() => {
      setShowLiveSuggestions(false);
    }, 200);
    // Reset animation state to start fresh
    setCharIndex(0);
    setIsDeleting(false);
    setDisplayText('');
  };

  useEffect(() => {
    // If there are no examples, skip effect
    if (!placeholderExamples || placeholderExamples.length === 0) return;

    // Stop animating when user is interacting with the input
    const inputEl = searchInpuRef?.current;
    const userIsTyping = !!inputEl && (document.activeElement === inputEl || (inputEl.value || '').length > 0);
    const shouldStopAnimation = userIsTyping || isInputFocused;

    if (shouldStopAnimation) return;

    const current = placeholderExamples[exampleIndex] || '';

    let timeoutId;
    if (!isDeleting && charIndex < current.length) {
      // Typing forward
      timeoutId = setTimeout(() => {
        setDisplayText(current.slice(0, charIndex + 1));
        setCharIndex(charIndex + 1);
      }, typingSpeedMs);
    } else if (!isDeleting && charIndex === current.length) {
      // Pause at full text, then start deleting
      timeoutId = setTimeout(() => {
        setIsDeleting(true);
      }, pauseAtEndMs);
    } else if (isDeleting && charIndex > 0) {
      // Deleting characters
      timeoutId = setTimeout(() => {
        setDisplayText(current.slice(0, charIndex - 1));
        setCharIndex(charIndex - 1);
      }, deletingSpeedMs);
    } else if (isDeleting && charIndex === 0) {
      // Switch to next example, pause briefly before typing again
      timeoutId = setTimeout(() => {
        setIsDeleting(false);
        setExampleIndex(i => (i + 1) % placeholderExamples.length);
      }, pauseBeforeStartMs);
    }

    return () => {
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, [placeholderExamples, exampleIndex, charIndex, isDeleting, typingSpeedMs, deletingSpeedMs, pauseAtEndMs, pauseBeforeStartMs, searchInpuRef, isInputFocused]);

  const placeholder = hidePlaceholder ? 'Rechercher' : (displayText || defaultBasePlaceholder);

  const onChange = location => {
    if (!isMainSearchTypeKeywords(appConfig) && location.selectedPlace) {
      // Note that we use `onSubmit` instead of the conventional
      // `handleSubmit` prop for submitting. We want to autosubmit
      // when a place is selected, and don't require any extra
      // validations for the form.
      onSubmit({ location });
      // blur search input to hide software keyboard
      searchInpuRef?.current?.blur();
    }
  };

  const onKeywordSubmit = values => {
    if (isMainSearchTypeKeywords(appConfig)) {
      if (activeSuggestionIndex >= 0 && suggestions[activeSuggestionIndex]) {
        const selected = suggestions[activeSuggestionIndex];
        if (typeof window !== 'undefined' && selected.url) {
          window.location.href = selected.url;
          return;
        }
      }
      onSubmit({ keywords: values.keywords });
      // blur search input to hide software keyboard
      searchInpuRef?.current?.blur();
    }
  };

  const handleSuggestionSelect = item => {
    setShowLiveSuggestions(false);
    setActiveSuggestionIndex(-1);
    if (item?.url && typeof window !== 'undefined') {
      window.location.href = item.url;
      return;
    }
    onSubmit({ keywords: item?.productTitle || keywordQuery });
    searchInpuRef?.current?.blur();
  };

  const handleKeywordChange = value => {
    setKeywordQuery(value);
    setActiveSuggestionIndex(-1);
    if (!value || value.trim().length < 2) {
      setSuggestions([]);
      if (!isMobileForm && recentlyViewedItems.length === 0) {
        setShowLiveSuggestions(false);
      } else {
        setShowLiveSuggestions(true);
      }
    } else {
      setShowLiveSuggestions(true);
    }
  };

  const handleKeywordKeyDown = event => {
    if (!showLiveSuggestions || suggestions.length === 0) {
      return;
    }

    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setActiveSuggestionIndex(prev => (prev + 1) % suggestions.length);
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      setActiveSuggestionIndex(prev => (prev <= 0 ? suggestions.length - 1 : prev - 1));
    } else if (event.key === 'Escape') {
      setShowLiveSuggestions(false);
      setActiveSuggestionIndex(-1);
    } else if (event.key === 'Enter' && activeSuggestionIndex >= 0) {
      event.preventDefault();
      handleSuggestionSelect(suggestions[activeSuggestionIndex]);
    }
  };

  useEffect(() => {
    if (!isMainSearchTypeKeywords(appConfig)) {
      return undefined;
    }

    const term = keywordQuery.trim();
    if (term.length < 2) {
      setIsLoadingSuggestions(false);
      setSuggestions([]);
      return undefined;
    }

    const requestId = requestCounterRef.current + 1;
    requestCounterRef.current = requestId;
    setIsLoadingSuggestions(true);

    const timeoutId = setTimeout(() => {
      searchSuggestions({ q: term, limit: 8 })
        .then(response => {
          if (requestCounterRef.current !== requestId) {
            return;
          }
          setSuggestions(response?.suggestions || []);
          setActiveSuggestionIndex(-1);
        })
        .catch(() => {
          if (requestCounterRef.current !== requestId) {
            return;
          }
          setSuggestions([]);
        })
        .finally(() => {
          if (requestCounterRef.current === requestId) {
            setIsLoadingSuggestions(false);
          }
        });
    }, 140);

    return () => {
      clearTimeout(timeoutId);
    };
  }, [appConfig, isMobileForm, keywordQuery]);

  useEffect(() => {
    if (!isMainSearchTypeKeywords(appConfig) || !showLiveSuggestions) {
      return;
    }

    if (keywordQuery.trim().length < 2) {
      refreshRecentlyViewed();
    }
  }, [appConfig, showLiveSuggestions, keywordQuery]);

  useEffect(() => {
    const shouldLockPage = isMobileForm && isMainSearchTypeKeywords(appConfig) && showLiveSuggestions;
    if (!shouldLockPage || typeof window === 'undefined') {
      if (pageLockRef.current.locked) {
        const previousScrollY = pageLockRef.current.scrollY || 0;
        document.body.style.position = '';
        document.body.style.top = '';
        document.body.style.left = '';
        document.body.style.right = '';
        document.body.style.width = '';
        document.body.style.overflow = '';
        document.documentElement.style.overflow = '';
        window.scrollTo(0, previousScrollY);
        pageLockRef.current = { scrollY: 0, locked: false };
        showTidioChat();
      }
      return undefined;
    }

    if (!pageLockRef.current.locked) {
      const scrollY = window.scrollY || 0;
      pageLockRef.current = { scrollY, locked: true };
      document.body.style.position = 'fixed';
      document.body.style.top = `-${scrollY}px`;
      document.body.style.left = '0';
      document.body.style.right = '0';
      document.body.style.width = '100%';
      document.body.style.overflow = 'hidden';
      document.documentElement.style.overflow = 'hidden';
      hideTidioChat();
    }

    return () => {
      if (pageLockRef.current.locked) {
        const previousScrollY = pageLockRef.current.scrollY || 0;
        document.body.style.position = '';
        document.body.style.top = '';
        document.body.style.left = '';
        document.body.style.right = '';
        document.body.style.width = '';
        document.body.style.overflow = '';
        document.documentElement.style.overflow = '';
        window.scrollTo(0, previousScrollY);
        pageLockRef.current = { scrollY: 0, locked: false };
        showTidioChat();
      }
    };
  }, [appConfig, isMobileForm, showLiveSuggestions]);

  const isKeywordsSearch = isMainSearchTypeKeywords(appConfig);
  const submit = isKeywordsSearch ? onKeywordSubmit : onSubmit;
  return (
    <FinalForm
      {...restOfProps}
      onSubmit={submit}
      render={formRenderProps => {
        const {
          rootClassName,
          className,
          desktopInputRoot,
          isMobile = false,
          handleSubmit,
        } = formRenderProps;
        const classes = classNames(rootClassName, className);
        const desktopInputRootClass = desktopInputRoot || css.desktopInputRoot;

        // Location search: allow form submit only when the place has changed
        const preventFormSubmit = e => e.preventDefault();
        const submitFormFn = isKeywordsSearch ? handleSubmit : preventFormSubmit;

        const keywordSearchWrapperClasses = classNames(
          css.keywordSearchWrapper,
          isMobile ? css.mobileInputRoot : desktopInputRootClass
        );
        const isLiveMenuVisible =
          isKeywordsSearch &&
          showLiveSuggestions &&
          (
            isMobile ||
            isLoadingSuggestions ||
            suggestions.length > 0 ||
            keywordQuery.trim().length >= 2 ||
            recentlyViewedItems.length > 0
          );
        const searchContainerClasses = classNames(css.searchFormContainer, {
          [css.searchFormContainerExpandedDesktop]: isLiveMenuVisible && !isMobile,
          [css.searchFormContainerMobileMenuOpen]: isLiveMenuVisible && isMobile,
        });

        return (
          <div className={searchContainerClasses}>
            <Form className={classes} onSubmit={submitFormFn} enforcePagePreloadFor="SearchPage">
              {isKeywordsSearch ? (
                <KeywordSearchField
                  keywordSearchWrapperClasses={keywordSearchWrapperClasses}
                  iconClass={classNames(isMobile ? css.mobileIcon : css.desktopIcon || css.icon)}
                  intl={intl}
                  isMobile={isMobile}
                  inputRef={searchInpuRef}
                  placeholder={placeholder || defaultBasePlaceholder}
                  onFocus={handleInputFocus}
                  onBlur={handleInputBlur}
                  onKeywordChange={handleKeywordChange}
                  onKeyDown={handleKeywordKeyDown}
                  uniqueId={uniqueInputId}
                />
              ) : (
                <LocationSearchField
                  desktopInputRootClass={desktopInputRootClass}
                  intl={intl}
                  isMobile={isMobile}
                  inputRef={searchInpuRef}
                  onLocationChange={onChange}
                  placeholder={placeholder || defaultBasePlaceholder}
                  onFocus={handleInputFocus}
                  onBlur={handleInputBlur}
                />
              )}
            </Form>

            <LiveSearchMegaMenu
              isVisible={isLiveMenuVisible}
              suggestions={suggestions}
              recentlyViewedItems={recentlyViewedItems}
              activeIndex={activeSuggestionIndex}
              isLoading={isLoadingSuggestions}
              isMobile={isMobile}
              hasQuery={keywordQuery.trim().length >= 2}
              onRequestClose={() => {
                setShowLiveSuggestions(false);
                setActiveSuggestionIndex(-1);
              }}
              onItemHover={setActiveSuggestionIndex}
              onItemSelect={handleSuggestionSelect}
            />
          </div>
        );
      }}
    />
  );
};

export default TopbarSearchForm;
